import { useMemo } from "react";

type FormatStyle = "currency" | "number";

/**
 * Abbreviated COP, for dashboards where the exact peso does not matter.
 *
 * Two scales: pesos below a million, then M (so 1.750.000.000 is "$1.750 M").
 * Never "B" — that is 10^9 in English, but a "billon" is 10^12 in Spanish, so
 * $6.80B was read as a thousand times the real figure.
 *
 * Not `Intl`'s `notation: "compact"`: in es-CO it turns 50.000 into "$50 k"
 * and formats 6.320.066.000 as "$6320,1 M" without the thousands separator.
 */
export const money = (value: number): string => {
  // One decimal below ten million so a $3,7 M canon does not round to $4 M.
  if (value >= 1e6)
    return `$${(value / 1e6).toLocaleString("es-CO", {
      maximumFractionDigits: value < 1e7 ? 1 : 0,
    })} M`;
  // Under a million there is nothing to abbreviate: show the pesos.
  return `$${Math.round(value).toLocaleString("es-CO")}`;
};

const useNumberFormatter = (
  locale = "es-CO",
  currency = "COP",
  style: FormatStyle = "currency"
) => {
  const formatter = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: style === "currency" ? "currency" : "decimal",
        currency: style === "currency" ? currency : undefined,
        minimumFractionDigits: 0,
      }),
    [locale, currency, style]
  );

  return (amount: number) => {
    return formatter.format(amount);
  };
};

export default useNumberFormatter;
